/**
 * Permission modules aligned with the trimmed comm app (sidebar + configuration hub).
 * Keep in sync with backend/src/permissions/constants.ts.
 */

export const PERMISSION_MODULE_KEYS = [
  'dashboard',
  'emails',
  'clients',
  'appointments',
  'todos',
  'users',
  'email_logs',
  'system_health',
  'configuration_settings',
  'deleted_files',
  'custom_fields',
  'email_accounts',
  'email_signatures',
  'email_rules',
  'company_email_templates',
  'search_folders',
  'database_browser',
  'activity_logs',
  'app_status',
  'app_duration',
  'p_country',
  'tax_authority',
  'tags',
] as const;

export type PermissionModuleKey = (typeof PERMISSION_MODULE_KEYS)[number];

export const PERMISSION_MODULE_LABELS: Record<PermissionModuleKey, string> = {
  dashboard: 'Dashboard',
  emails: 'Emails',
  clients: 'Address Book',
  appointments: 'Appointments',
  todos: 'Todos',
  users: 'Users',
  email_logs: 'Sync Logs',
  system_health: 'Sync Status',
  configuration_settings: 'Settings',
  deleted_files: 'File Storage',
  custom_fields: 'Custom Fields',
  email_accounts: 'Email Accounts',
  email_signatures: 'Signatures',
  email_rules: 'Email Rules',
  company_email_templates: 'Company Library',
  search_folders: 'Smart Folders',
  database_browser: 'Database Browser',
  activity_logs: 'Activity Logs',
  app_status: 'Appointment Status',
  app_duration: 'Appointment Durations',
  p_country: 'Countries',
  tax_authority: 'Tax Authorities',
  tags: 'Tags',
};

/** One row on the permissions form; label matches sidebar where applicable. */
export interface PermissionGroupItem {
  moduleKey: PermissionModuleKey;
  label: string;
  hint?: string;
}

export interface PermissionGroup {
  title: string;
  items: PermissionGroupItem[];
}

/** Short helper text on the permissions form (view vs edit semantics). */
export const PERMISSION_MODULE_HINTS: Partial<Record<PermissionModuleKey, string>> = {
  system_health:
    'View: Sync status page. Edit: full System Health admin (server diagnostics, pause/resume sync).',
  emails: 'View, compose, and read mail. Edit also allows changing assignment and workflow on messages you can access.',
  company_email_templates: 'Company template library (configuration hub).',
};

/** Groups mirror the sidebar (Settings children + configuration hub parameters). */
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    title: 'Dashboard',
    items: [{ moduleKey: 'dashboard', label: 'Dashboard' }],
  },
  {
    title: 'Communication',
    items: [
      { moduleKey: 'emails', label: 'Emails' },
      { moduleKey: 'emails', label: 'Library', hint: 'Same permission as Emails.' },
      { moduleKey: 'clients', label: 'Address Book' },
    ],
  },
  {
    title: 'Work Management',
    items: [
      { moduleKey: 'appointments', label: 'Appointments' },
      { moduleKey: 'todos', label: 'Todos' },
      {
        moduleKey: 'system_health',
        label: 'Sync status',
        hint: 'Read-only sync status page in the main sidebar.',
      },
    ],
  },
  {
    title: 'Settings – System',
    items: [
      { moduleKey: 'users', label: 'Users' },
      { moduleKey: 'configuration_settings', label: 'Settings' },
      { moduleKey: 'custom_fields', label: 'Custom Fields' },
      { moduleKey: 'deleted_files', label: 'File Storage', hint: 'Files storage under configuration hub.' },
    ],
  },
  {
    title: 'Settings – Monitoring',
    items: [
      { moduleKey: 'email_logs', label: 'Sync Logs' },
      {
        moduleKey: 'system_health',
        label: 'System Health',
        hint: 'Full admin diagnostics. Requires edit on this module.',
      },
      { moduleKey: 'activity_logs', label: 'Activity Logs' },
    ],
  },
  {
    title: 'Settings – Email Setup',
    items: [
      { moduleKey: 'email_accounts', label: 'Email Accounts' },
      { moduleKey: 'email_signatures', label: 'Signatures' },
      { moduleKey: 'search_folders', label: 'Smart Folders' },
      { moduleKey: 'email_rules', label: 'Email Rules' },
      { moduleKey: 'company_email_templates', label: 'Company Library', hint: 'Template library in configuration hub.' },
    ],
  },
  {
    title: 'Settings – Tools',
    items: [
      { moduleKey: 'database_browser', label: 'Database Browser' },
      { moduleKey: 'clients', label: 'Import Organizations', hint: 'Same permission as Address Book.' },
    ],
  },
  {
    title: 'Parameters',
    items: [
      { moduleKey: 'app_status', label: 'Appointment Status' },
      { moduleKey: 'app_duration', label: 'Appointment Durations' },
      { moduleKey: 'p_country', label: 'Countries' },
      { moduleKey: 'tax_authority', label: 'Tax Authorities' },
      { moduleKey: 'tags', label: 'Tags' },
    ],
  },
];

export function uniquePermissionKeys(items: PermissionGroupItem[]): PermissionModuleKey[] {
  return Array.from(new Set(items.map((item) => item.moduleKey))) as PermissionModuleKey[];
}

export interface PermissionRow {
  moduleKey: string;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export function isPermissionModuleKey(key: string): key is PermissionModuleKey {
  return (PERMISSION_MODULE_KEYS as readonly string[]).includes(key);
}

export function permissionModuleLabel(key: string): string {
  if (isPermissionModuleKey(key)) return PERMISSION_MODULE_LABELS[key];
  return key.replace(/_/g, ' ');
}

/** Normalize API rows to the current app module list (drops removed modules). */
export function normalizePermissionRows(rows: PermissionRow[]): PermissionRow[] {
  const map = new Map(rows.map((row) => [row.moduleKey, row]));
  return PERMISSION_MODULE_KEYS.map((moduleKey) => {
    const row = map.get(moduleKey);
    return {
      moduleKey,
      canView: row?.canView ?? false,
      canEdit: row?.canEdit ?? false,
      canDelete: row?.canDelete ?? false,
    };
  });
}

/**
 * Maps sidebar path (or path prefix) to backend permission module key.
 */
export const PATH_TO_MODULE_KEY: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/ai-chat': 'dashboard',
  '/ai-prompts': 'configuration_settings',
  '/ai-usage': 'configuration_settings',
  '/emails': 'emails',
  '/library': 'emails',
  '/file-send': 'emails',
  '/email-accounts': 'email_accounts',
  '/email-signatures': 'email_signatures',
  '/smart-folders': 'search_folders',
  '/email-rules': 'email_rules',
  '/admin/email-logs': 'email_logs',
  '/system-health': 'system_health',
  '/admin/system-health': 'system_health',
  '/todos': 'todos',
  '/todos_monthly_view': 'todos',
  '/clients': 'clients',
  '/appointments': 'appointments',
  '/tax_authority': 'tax_authority',
  '/p_country': 'p_country',
  '/app_status': 'app_status',
  '/duration': 'app_duration',
  '/tags': 'tags',
  '/admin/users': 'users',
  '/settings': 'configuration_settings',
  '/admin/settings/files-storage': 'deleted_files',
  '/admin/settings/deleted-files': 'deleted_files',
  '/admin/settings/system-email-templates': 'configuration_settings',
  '/admin/settings/email-notifications': 'configuration_settings',
  '/admin/settings/google-sync': 'configuration_settings',
  '/configuration-settings': 'configuration_settings',
  '/parameters': 'app_status',
  '/activity-logs': 'activity_logs',
  '/custom-fields': 'custom_fields',
  '/tools/database-browser': 'database_browser',
  '/tools/import-organizations': 'clients',
};

export function getModuleKeyForPath(path: string): string | undefined {
  const normalized = path.split('?')[0];
  if (PATH_TO_MODULE_KEY[normalized]) return PATH_TO_MODULE_KEY[normalized];
  const segment = normalized.split('/').filter(Boolean)[0];
  if (!segment) return undefined;
  const byPrefix = Object.entries(PATH_TO_MODULE_KEY).find(
    ([p]) => p.startsWith('/' + segment) || normalized.startsWith(p),
  );
  return byPrefix?.[1];
}
